<script>
export default {
    name: 'Discount',
    data() {
        return {
            loading: false,
            minimalSecurityDiscount: null,
            priceObservatoryEnabled: false,
            discounts: [],
            dialogVisible: false,
            editingDiscount: null,
            discountForm: {
                id: null,
                start_date: '',
                end_date: '',
                title: '',
                value: null
            },
            formRules: {
                start_date: [
                    { required: true, message: 'La date de début est requise', trigger: 'blur' }
                ],
                end_date: [
                    { required: true, message: 'La date de fin est requise', trigger: 'blur' }
                ],
                title: [
                    { required: true, message: 'Le libellé est requis', trigger: 'blur' }
                ],
                value: [
                    { required: true, message: 'La remise est requise', trigger: 'blur' },
                    { type: 'number', message: 'La remise doit être un nombre', trigger: 'blur' }
                ]
            },
            pickerOptions: {
                format: 'dd/MM/yyyy HH:mm'
            },
            discountLogs: [],
            logsCurrentPage: 1,
            logsPerPage: 15,
            logsTotal: 0,
            shopIdFilter: null
        };
    },
    mounted() {
        this.fetchDiscounts();
        this.fetchDiscountLogs();
    },
    methods: {
        async fetchDiscounts() {
            this.loading = true;
            try {
                const response = await axios.get(`/discounts`);
                this.discounts = response.data.discounts || [];
                this.minimalSecurityDiscount = response.data.minimal_security_discount;
                this.priceObservatoryEnabled = response.data.priceobservatory_enabled || false;
            } catch (error) {
                this.$message.error('Erreur lors du chargement des remises');
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
        },
        openEditDialog(discount = null) {
            if (discount) {
                this.editingDiscount = discount;
                this.discountForm = {
                    id: discount.id,
                    start_date: discount.start_date,
                    end_date: discount.end_date,
                    title: discount.title,
                    value: parseFloat(discount.value)
                };
            } else {
                this.editingDiscount = null;
                this.discountForm = {
                    id: null,
                    start_date: '',
                    end_date: '',
                    title: '',
                    value: null
                };
            }
            this.dialogVisible = true;
        },
        async saveDiscount() {
            this.$refs.discountFormRef.validate(async (valid) => {
                if (!valid) return false;

                this.loading = true;
                try {
                    if (this.discountForm.id) {
                        await axios.put(`/discounts/${this.discountForm.id}`, this.discountForm);
                        this.$message.success('Remise mise à jour avec succès');
                    } else {
                        await axios.post(`/discounts`, this.discountForm);
                        this.$message.success('Remise créée avec succès');
                    }
                    this.dialogVisible = false;
                    await this.fetchDiscounts();
                } catch (error) {
                    this.$message.error('Erreur lors de la sauvegarde');
                    console.error(error);
                } finally {
                    this.loading = false;
                }
            });
        },
        async deleteDiscount(discountId) {
            try {
                await this.$confirm('Êtes-vous sûr de vouloir supprimer cette remise ?', 'Confirmation', {
                    confirmButtonText: 'Supprimer',
                    cancelButtonText: 'Annuler',
                    type: 'warning'
                });

                this.loading = true;
                await axios.delete(`/discounts/${discountId}`);
                this.$message.success('Remise supprimée avec succès');
                await this.fetchDiscounts();
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('Erreur lors de la suppression');
                    console.error(error);
                }
            } finally {
                this.loading = false;
            }
        },
        async updateMinimalDiscount() {
            try {
                await this.$prompt('Nouvelle valeur de remise minimale de sécurité (%)', 'Modifier', {
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Annuler',
                    inputPattern: /^\d+(\.\d+)?$/,
                    inputErrorMessage: 'Veuillez entrer un nombre valide',
                    inputValue: this.minimalSecurityDiscount
                }).then(async ({ value }) => {
                    this.loading = true;
                    await axios.put(`/settings`, {
                        key: 'minimal_security_discount',
                        value: parseFloat(value)
                    });
                    this.minimalSecurityDiscount = parseFloat(value);
                    this.$message.success('Remise minimale mise à jour');
                    this.loading = false;
                });
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('Erreur lors de la mise à jour');
                    console.error(error);
                    this.loading = false;
                }
            }
        },
        async fetchDiscountLogs(page = 1) {
            this.loading = true;
            try {
                const params = {
                    page: page,
                    per_page: this.logsPerPage
                };

                if (this.shopIdFilter) {
                    params.shop_id = this.shopIdFilter;
                }

                const response = await axios.get(`/repricing-logs`, { params });
                this.discountLogs = response.data.data || [];
                this.logsTotal = response.data.total || 0;
                this.logsCurrentPage = response.data.current_page || 1;
            } catch (error) {
                this.$message.error('Erreur lors du chargement des logs');
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        handleLogsPageChange(page) {
            this.fetchDiscountLogs(page);
        },
        async togglePriceObservatory(value) {
            this.loading = true;
            try {
                await axios.put(`/settings`, {
                    key: 'priceobservatory_enabled',
                    value: value
                });
                this.priceObservatoryEnabled = value;
                this.$message.success(`Observatoire des prix ${value ? 'activé' : 'désactivé'}`);
            } catch (error) {
                this.$message.error('Erreur lors de la mise à jour');
                console.error(error);
                this.priceObservatoryEnabled = !value;
            } finally {
                this.loading = false;
            }
        }
    }
};
</script>

<template>
    <div v-loading="loading" class="product-discount">
        <el-card shadow="never">
            <div slot="header" class="clearfix">
                <span class="discount-header-title">
                    Remise minimale de sécurité : <strong>{{ minimalSecurityDiscount }}%</strong>

                </span>
                <el-button size="small" style="padding: 3px 8px" type="text"
                           @click="updateMinimalDiscount">
                    Modifier
                </el-button>
                <div style="float: right;">
                    <span style="margin-right: 15px; font-size: 14px;">
                        Observatoire des prix :
                        <el-switch
                            v-model="priceObservatoryEnabled"
                            :active-value="true"
                            :inactive-value="false"
                            active-color="#13ce66"
                            inactive-color="#ff4949"
                            @change="togglePriceObservatory">
                        </el-switch>
                    </span>

                </div>
            </div>

            <div class="discount-actions mb-3">
                <el-button icon="el-icon-plus" size="small" type="primary" @click="openEditDialog()">
                    Ajouter une remise
                </el-button>
            </div>

            <el-table
                :data="discounts"
                :empty-text="'Aucune remise configurée'"
                border
                stripe
                style="width: 100%">
                <el-table-column
                    label="Date de début"
                    prop="start_date"
                    width="180">
                    <template slot-scope="scope">
                        {{ formatDate(scope.row.start_date) }}
                    </template>
                </el-table-column>
                <el-table-column
                    label="Date de fin"
                    prop="end_date"
                    width="180">
                    <template slot-scope="scope">
                        {{ formatDate(scope.row.end_date) }}
                    </template>
                </el-table-column>
                <el-table-column
                    label="Libellé"
                    min-width="200"
                    prop="title">
                </el-table-column>
                <el-table-column
                    label="Remise"
                    prop="value"
                    width="100">
                    <template slot-scope="scope">
                        {{ scope.row.value }}%
                    </template>
                </el-table-column>
                <el-table-column
                    align="center"
                    label="Action"
                    width="180">
                    <template slot-scope="scope">
                        <el-button
                            icon="el-icon-edit"
                            size="small"
                            type="text"
                            @click="openEditDialog(scope.row)">
                        </el-button>
                        <el-button
                            icon="el-icon-delete"
                            size="small"
                            style="color: #F56C6C"
                            type="text"
                            @click="deleteDiscount(scope.row.id)">
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <el-dialog
            :title="editingDiscount ? 'Modifier la remise' : 'Ajouter une remise'"
            :visible.sync="dialogVisible"
            append-to-body
            width="600px">
            <el-form
                ref="discountFormRef"
                :model="discountForm"
                :rules="formRules"
                label-width="150px">
                <el-form-item label="Date de début" prop="start_date">
                    <el-date-picker
                        v-model="discountForm.start_date"
                        format="dd/MM/yyyy HH:mm"
                        placeholder="Sélectionner"
                        style="width: 100%"
                        type="datetime"
                        value-format="yyyy-MM-dd HH:mm:ss">
                    </el-date-picker>
                </el-form-item>
                <el-form-item label="Date de fin" prop="end_date">
                    <el-date-picker
                        v-model="discountForm.end_date"
                        format="dd/MM/yyyy HH:mm"
                        placeholder="Sélectionner"
                        style="width: 100%"
                        type="datetime"
                        value-format="yyyy-MM-dd HH:mm:ss">
                    </el-date-picker>
                </el-form-item>
                <el-form-item label="Libellé" prop="label">
                    <el-input v-model="discountForm.title"
                              placeholder="Ex: French Days, Mois anniversaire..."></el-input>
                </el-form-item>
                <el-form-item label="Remise (%)" prop="discount">
                    <el-input-number
                        v-model="discountForm.value"
                        :max="100"
                        :min="0"
                        :precision="2"
                        :step="1.0"
                        style="width: 100%">
                    </el-input-number>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <el-button @click="dialogVisible = false">Annuler</el-button>
                <el-button type="primary" @click="saveDiscount">Enregistrer</el-button>
            </span>
        </el-dialog>

        <el-card shadow="never" style="margin-top: 20px">
            <div slot="header" class="clearfix">
                <span class="discount-header-title">Historique des modifications de prix</span>
            </div>

            <div style="margin-bottom: 15px;">
                <el-form :inline="true" size="small">
                    <el-form-item label="Filtrer par Shop ID">
                        <el-input
                            v-model="shopIdFilter"
                            clearable
                            placeholder="Ex: 34"
                            style="width: 200px"
                            @clear="fetchDiscountLogs(1)"
                            @keyup.enter.native="fetchDiscountLogs(1)">
                        </el-input>
                    </el-form-item>
                    <el-form-item>
                        <el-button icon="el-icon-search" type="primary" @click="fetchDiscountLogs(1)">
                            Rechercher
                        </el-button>
                    </el-form-item>
                </el-form>
            </div>

            <el-table
                :data="discountLogs"
                :empty-text="'Aucun log disponible'"
                border
                stripe
                style="width: 100%">
                <el-table-column
                    label="Date"
                    prop="created_at"
                    width="180">
                    <template slot-scope="scope">
                        {{ formatDate(scope.row.created_at) }}
                    </template>
                </el-table-column>
                <el-table-column
                    label="Produit ID"
                    prop="product_id"
                    width="120">
                </el-table-column>
                <el-table-column
                    label="Shop ID"
                    prop="product.shop_id"
                    width="100">
                    <template slot-scope="scope">
                        {{ scope.row.product ? scope.row.product.shop_id : '-' }}
                    </template>
                </el-table-column>
                <el-table-column
                    label="Coefficient"
                    prop="coef"
                    width="100">
                    <template slot-scope="scope">
                        {{ scope.row.coef ? scope.row.coef.toFixed(6) : '-' }}
                    </template>
                </el-table-column>
                <el-table-column
                    label="Nouveau prix"
                    prop="new_price"
                    width="120">
                    <template slot-scope="scope">
                        {{ scope.row.new_price ? scope.row.new_price.toFixed(2) + ' €' : '-' }}
                    </template>
                </el-table-column>
                <el-table-column
                    label="Exclusion opé web ?"
                    prop="featured"
                    width="150">
                    <template slot-scope="scope">
                        {{ scope.row.featured ? 'Oui' : 'Non' }}
                    </template>
                </el-table-column>
                <el-table-column
                    label="Statut"
                    min-width="150"
                    prop="status">
                </el-table-column>
                <el-table-column
                    label="Erreur"
                    min-width="150"
                    prop="error">
                </el-table-column>
            </el-table>

            <div style="margin-top: 20px; text-align: center">
                <el-pagination
                    :current-page="logsCurrentPage"
                    :page-size="logsPerPage"
                    :total="logsTotal"
                    layout="total, prev, pager, next"
                    @current-change="handleLogsPageChange">
                </el-pagination>
            </div>
        </el-card>
    </div>
</template>

<style lang="scss" scoped>
.product-discount {
    .discount-header-title {
        font-size: 16px;
    }

    .discount-actions {
        margin-bottom: 15px;
    }

    .el-table {
        font-size: 13px;
    }
}
</style>
